// File: src/components/Reveal/RevealChatHeader.jsx
import React, { useMemo } from "react";
import { TbNavigationPin } from "react-icons/tb";
import { LuChevronRight } from "react-icons/lu";

/* ───────────────────────── Helpers (Navigate-style) ───────────────────────── */
function partsFromMs(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return { h: "00", m: "00", s: "00" };
  const t = Math.floor(ms / 1000);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return {
    h: String(h).padStart(2, "0"),
    m: String(m).padStart(2, "0"),
    s: String(s).padStart(2, "0"),
  };
}

/* ───────────────────────── Styles ───────────────────────── */
const S = {
  wrap: {
    boxSizing: "border-box",
    maxWidth: 680,
    width: "94%",
    margin: "12px auto 8px",
    padding: "0 12px",
    textAlign: "center",
    color: "#0b0b0b",
    position: "relative",
    zIndex: 30,
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  /* Headline row => FIND + pill ON ONE LINE */
  titleRow: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    whiteSpace: "nowrap",           // keep on one line
  },
  titleText: {
    display: "inline-block",
    margin: 0,
    fontSize: "1.85rem",
    fontWeight: 900,
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
  },

  /* Grey name pill (match CodeVerification pill style, scaled up a touch) */
  namePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px 6px 6px",    // similar to code verification pill but a bit bigger
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#0f172a",
    lineHeight: 1,
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },
  nameAvatar: {
    width: 24,                      // code verification used ~18; larger to match headline scale
    height: 24,
    borderRadius: "50%",
    objectFit: "cover",
    display: "block",
    border: "1px solid #fff",
  },
  nameText: {
    fontSize: "1rem",               // larger than .85rem; still understated
    fontWeight: 900,
    letterSpacing: "-0.01em",
    whiteSpace: "nowrap",
  },

  /* Uber-like toggle (appears only when two partners) */
  switcher: {
    row: { display: "flex", gap: 8, justifyContent: "center", marginTop: 6, marginBottom: 8 },
    chip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 12px",
      borderRadius: 999,
      border: "1px solid #E5E7EB",
      background: "#fff",
      color: "#0b0b0b",
      fontWeight: 800,
      cursor: "pointer",
    },
    chipActive: { border: "1px solid #0b0b0b", background: "#0b0b0b", color: "#fff" },
    dot: (active) => ({
      width: 8,
      height: 8,
      borderRadius: 999,
      background: active ? "#22c55e" : "#e5e7eb",
    }),
  },

  /* Helper copy */
  sub: {
    marginTop: 10,
    fontSize: ".9rem",
    color: "#6b7280",
    fontWeight: 500,
    lineHeight: 1.45,
    marginBottom: 0,
  },

  /* Timer */
  timerWrap: {
    marginTop: 16,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  timerLabel: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    background: "#fff",
    border: "1px solid #e5e7eb",
    fontSize: ".7rem",
    color: "#374151",
    fontWeight: 800,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    marginBottom: 8,
  },
  digitsRow: { display: "flex", alignItems: "baseline", gap: 4 },
  digit: {
    display: "inline-block",
    fontSize: "2.6rem",
    lineHeight: 1,
    fontWeight: 900,
    color: "#0f172a",
  },
  colon: {
    fontSize: "2rem",
    fontWeight: 900,
    color: "#9ca3af",
    lineHeight: 1,
    padding: "8px 4px",
    transform: "translateY(-5px)",
  },

  /* CTA */
  cta: {
    btn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: "8px 12px",
      borderRadius: 999,
      background: "#fff",
      color: "#0b0b0b",
      fontWeight: 900,
      letterSpacing: 0.2,
      border: "1px solid #0b0b0b",
      marginTop: 10,
      cursor: "pointer",
      transition: "background .15s ease, transform .06s ease",
      boxShadow: "0 0 0 0 rgba(0,0,0,0)",
    },
  },
};

/* ───────────────────────── Component ───────────────────────── */
export default function RevealChatHeader({
  // Partner info for the name pill
  partnerName = "Your partner",
  partnerAvatar = "/default-avatar.png",

  // Toggle (trio case: you have 2 chats)
  matchCount = 1,
  activeIndex = 0,
  onSwitchPartner,

  // Timer + copy
  subheading = "Say hello and coordinate where to meet 👋",
  timeLeftMs = 0,
  showHours,
  onFoundPartner,
}) {
  const t = useMemo(() => partsFromMs(timeLeftMs), [timeLeftMs]);
  const hasHours = typeof showHours === "boolean" ? showHours : t.h !== "00";

  return (
    <div style={S.wrap}>
      {/* Toggle above the pill when user has 2 partners */}
      {matchCount > 1 && (
        <div style={S.switcher.row} role="tablist" aria-label="Switch partner">
          {[0, 1].slice(0, matchCount).map((i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === activeIndex}
              onClick={() => onSwitchPartner && onSwitchPartner(i)}
              style={{ ...S.switcher.chip, ...(i === activeIndex ? S.switcher.chipActive : {}) }}
            >
              <span style={S.switcher.dot(i === activeIndex)} />
              {`Partner ${i + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* One-line headline: Find + pill */}
      <div style={S.titleRow}>
        <span style={S.titleText}>Find</span>
        <span style={S.namePill} aria-live="polite">
          <img
            alt=""
            src={partnerAvatar || "/default-avatar.png"}
            onError={(e) => (e.currentTarget.src = "/default-avatar.png")}
            style={S.nameAvatar}
          />
          <span style={S.nameText}>{partnerName}</span>
        </span>
      </div>

      <div style={S.sub}>{subheading}</div>

      {/* Timer */}
      <div style={S.timerWrap}>
        <div style={S.timerLabel}>Time Ending in</div>
        <div style={S.digitsRow} aria-live="polite">
          {hasHours && (
            <>
              <span style={S.digit}>{t.h[0]}</span>
              <span style={S.digit}>{t.h[1]}</span>
              <span style={S.colon}>:</span>
            </>
          )}
          <span style={S.digit}>{t.m[0]}</span>
          <span style={S.digit}>{t.m[1]}</span>
          <span style={S.colon}>:</span>
          <span style={S.digit}>{t.s[0]}</span>
          <span style={S.digit}>{t.s[1]}</span>
        </div>
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={onFoundPartner}
        style={S.cta.btn}
        aria-label="I found my partner"
      >
        <TbNavigationPin size={18} />
        <span>Found partners</span>
        <LuChevronRight size={18} />
      </button>
    </div>
  );
}
